import { FC, useEffect, useState } from "react";
import { Brain, Sparkles, Zap, Layers, Network, Code, CheckCircle, Loader } from "lucide-react";

interface ThinkingAnimationProps {
  hasLoadingDiv: boolean;
  hasDiagram: boolean;
  completedSections?: string[];
}

const thinkingMessages = [
  { icon: Brain, text: "Thinking...", color: "purple" },
  { icon: Sparkles, text: "Architecturing...", color: "blue" },
  { icon: Layers, text: "Evaluating best tech...", color: "indigo" },
  { icon: Network, text: "Picking best solution...", color: "cyan" },
  { icon: Code, text: "Designing patterns...", color: "violet" },
  { icon: Zap, text: "Optimizing structure...", color: "amber" },
  { icon: Brain, text: "Analyzing requirements...", color: "pink" },
  { icon: Layers, text: "Planning microservices...", color: "teal" }
];

const ThinkingAnimation: FC<ThinkingAnimationProps> = ({ hasLoadingDiv, hasDiagram, completedSections = [] }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [step, setStep] = useState<1 | 2>(1);

  // Rotate through thinking messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % thinkingMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Update step based on loading div and diagram status
  useEffect(() => {
    if (hasLoadingDiv) {
      setStep(1); // Still generating architecture
    } else if (!hasDiagram) {
      setStep(2); // Architecture done, generating diagram
    }
  }, [hasLoadingDiv, hasDiagram]);

  const currentMessage = thinkingMessages[currentMessageIndex];
  const Icon = currentMessage.icon;

  const getColorClasses = (color: string) => {
    const colors: Record<string, any> = {
      purple: { bg: "from-purple-500 to-purple-600", text: "text-purple-600", border: "border-purple-200", glow: "bg-purple-500/20" },
      blue: { bg: "from-blue-500 to-blue-600", text: "text-blue-600", border: "border-blue-200", glow: "bg-blue-500/20" },
      indigo: { bg: "from-indigo-500 to-indigo-600", text: "text-indigo-600", border: "border-indigo-200", glow: "bg-indigo-500/20" },
      cyan: { bg: "from-cyan-500 to-cyan-600", text: "text-cyan-600", border: "border-cyan-200", glow: "bg-cyan-500/20" },
      violet: { bg: "from-violet-500 to-violet-600", text: "text-violet-600", border: "border-violet-200", glow: "bg-violet-500/20" },
      amber: { bg: "from-amber-500 to-amber-600", text: "text-amber-600", border: "border-amber-200", glow: "bg-amber-500/20" },
      pink: { bg: "from-pink-500 to-pink-600", text: "text-pink-600", border: "border-pink-200", glow: "bg-pink-500/20" },
      teal: { bg: "from-teal-500 to-teal-600", text: "text-teal-600", border: "border-teal-200", glow: "bg-teal-500/20" }
    };
    return colors[color] || colors.purple;
  };

  const colors = getColorClasses(currentMessage.color);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 via-white to-gray-50 border border-slate-200 shadow-lg p-8">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-10 -right-10 w-40 h-40 ${colors.glow} rounded-full blur-2xl animate-pulse`}></div>
        <div className={`absolute -bottom-10 -left-10 w-32 h-32 ${colors.glow} rounded-full blur-2xl animate-pulse delay-1000`}></div>
      </div>

      <div className="relative space-y-8">
        {/* Timeline */}
        <div className="flex items-center justify-center max-w-3xl mx-auto px-4">
          <div className="flex items-center w-full">
            {/* Step 1: Architecture */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                step === 1
                  ? `bg-gradient-to-r ${colors.bg} shadow-lg animate-pulse`
                  : "bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg"
              }`}>
                {step === 1 ? (
                  <Loader className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                )}
              </div>
              <div>
                <div className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${step === 1 ? colors.text : "text-green-600"}`}>
                  {step === 1 ? "Generating Architecture" : "Architecture Ready"}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500">Step 1</div>
              </div>
            </div>

            {/* Connector - Centered and Full Width */}
            <div className="flex-1 px-4 sm:px-6 md:px-8">
              <div className="h-1 bg-gray-200 rounded-full relative overflow-hidden">
                <div
                  className={`absolute top-0 left-0 h-full transition-all duration-1000 rounded-full ${
                    step === 2 ? "w-full bg-gradient-to-r from-green-500 to-blue-500" : "w-0"
                  }`}
                ></div>
              </div>
            </div>

            {/* Step 2: Diagram */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                step === 2
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg animate-pulse"
                  : "bg-gray-300"
              }`}>
                {step === 2 ? (
                  <Loader className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-spin" />
                ) : (
                  <span className="text-white font-bold text-xs sm:text-sm">2</span>
                )}
              </div>
              <div>
                <div className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${step === 2 ? "text-blue-600" : "text-gray-500"}`}>
                  {step === 2 ? "Generating Diagram" : "Diagram Pending"}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500">Step 2</div>
              </div>
            </div>
          </div>
        </div>

        {/* Thinking Message with Icon */}
        <div className="text-center space-y-6">
          {/* Animated Icon */}
          <div className="relative inline-block">
            <div className={`absolute inset-0 ${colors.glow} rounded-full blur-xl`}></div>
            <div className={`relative bg-gradient-to-br ${colors.bg} rounded-full p-6 transition-all duration-500 transform hover:scale-110`}>
              <Icon className="w-12 h-12 text-white animate-pulse" />
            </div>
          </div>

          {/* Message Text with fade animation */}
          <div className="min-h-[40px] flex items-center justify-center">
            <h3 className={`text-2xl font-bold ${colors.text} transition-opacity duration-500 animate-fadeIn`}>
              {currentMessage.text}
            </h3>
          </div>

          {/* Pulsing dots */}
          <div className="flex justify-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${colors.bg.replace("from-", "bg-")} animate-bounce`}></div>
            <div className={`w-3 h-3 rounded-full ${colors.bg.replace("from-", "bg-")} animate-bounce delay-100`}></div>
            <div className={`w-3 h-3 rounded-full ${colors.bg.replace("from-", "bg-")} animate-bounce delay-200`}></div>
          </div>

          {/* Current step description */}
          <p className="text-gray-600 text-lg max-w-md mx-auto">
            {step === 1
              ? "AI is analyzing your requirements and designing the perfect architecture..."
              : "Creating visual diagram of your architecture..."}
          </p>
        </div>

        {/* Completed Sections */}
        {completedSections.length > 0 && (
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm p-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                Completed Sections
              </h4>
              <div className="space-y-2">
                {completedSections.map((section, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 text-sm text-gray-600 animate-fadeIn"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="flex-1">{section}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className={`h-full rounded-full transition-all duration-1000 bg-gradient-to-r ${colors.bg} animate-pulse`}
              style={{ width: step === 1 ? "50%" : "90%" }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThinkingAnimation;
